/**
 * ModuleTemplateConfigPage.jsx
 * 
 * Simplified configuration page for assigning templates to predefined module contexts.
 * The contexts are hardcoded based on the system's module structure.
 * Users can only assign templates to existing contexts - they cannot create new contexts.
 * 
 * Features:
 * - Lists all predefined modules and their contexts
 * - Allows assigning templates to each context via dropdown
 * - Bulk save functionality
 * - Module filtering
 * - Clear visual feedback for pending changes
 * - Initialize button to seed predefined contexts into database
 * 
 * Predefined Contexts:
 * - General: Ad-Hoc
 * - Operations: SIPA, Non-Compliance, CM Daily Checklist, ROM Daily Checklist
 * - Procurement: Vendor Application, Procurement Pack
 * - Finance: Payment Trackers
 * - Leasing: Lease Renewal
 * - IT: Support Ticket, Change Request, Onboarding, Offboarding, Software Dev Request
 * - Projects: Project Task, Project SubTask
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/ModuleTemplateConfigStyles.scss';
import { FaPuzzlePiece, FaSpinner, FaDatabase, FaInfoCircle,
    FaPlusCircle, FaHardHat, FaShoppingCart, FaChartLine, FaFileSignature,
    FaLaptopCode, FaProjectDiagram, FaGavel, FaUsers, FaCog, FaClipboardCheck,
    FaExclamationTriangle, FaTasks, FaCogs, FaWrench, FaUserPlus, FaMoneyBillWave,
    FaHeadset, FaExchangeAlt, FaUserCheck, FaUserTimes, FaCode, FaNetworkWired,
    FaStream
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Map of FA icon class names to React Icon components.
 * Used to render icons stored in the database as FA class strings.
 */
const FA_ICON_MAP = {
    'fa-plus-circle': FaPlusCircle,
    'fa-hard-hat': FaHardHat,
    'fa-shopping-cart': FaShoppingCart,
    'fa-chart-line': FaChartLine,
    'fa-file-signature': FaFileSignature,
    'fa-laptop-code': FaLaptopCode,
    'fa-project-diagram': FaProjectDiagram,
    'fa-gavel': FaGavel,
    'fa-users': FaUsers,
    'fa-puzzle-piece': FaPuzzlePiece,
    'fa-cog': FaCog,
    'fa-cogs': FaCogs,
    'fa-clipboard-check': FaClipboardCheck,
    'fa-exclamation-triangle': FaExclamationTriangle,
    'fa-tasks': FaTasks,
    'fa-wrench': FaWrench,
    'fa-user-plus': FaUserPlus,
    'fa-money-bill-wave': FaMoneyBillWave,
    'fa-headset': FaHeadset,
    'fa-exchange-alt': FaExchangeAlt,
    'fa-user-check': FaUserCheck,
    'fa-user-times': FaUserTimes,
    'fa-code': FaCode,
    'fa-network-wired': FaNetworkWired,
    'fa-stream': FaStream,
};

/**
 * Resolve a FA icon class name to a React Icon component.
 * Falls back to FaCog if the icon class is not mapped.
 */
const resolveIcon = (iconClass) => {
    const IconComponent = FA_ICON_MAP[iconClass];
    return IconComponent ? <IconComponent /> : <FaCog />;
};

/**
 * Predefined module contexts
 * New contexts should be added here by developers as the system grows.
 * This ensures consistency and prevents users from creating arbitrary contexts.
 */
const PREDEFINED_CONTEXTS = [
    // General - for ad-hoc items created directly from Action Items page
    { moduleKey: 'General', moduleDisplayName: 'General', contextKey: 'AdHoc', contextDisplayName: 'Ad-Hoc Action Items', contextDescription: 'Action items created manually from the Action Items page without a specific source module.', icon: 'fa-plus-circle', sortOrder: 1 },

    // Operations - inspections, checklists, compliance
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'SIPA', contextDisplayName: 'SIPA Inspections', contextDescription: 'Action items generated from SIPA (Standard Inspection Property Assessment) sub-section findings.', icon: 'fa-clipboard-check', sortOrder: 1 },
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'NonCompliance', contextDisplayName: 'Non-Compliance Reports', contextDescription: 'Action items for tenant non-compliance issues such as lease violations, safety concerns, or maintenance requests.', icon: 'fa-exclamation-triangle', sortOrder: 2 },
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'CMDailyChecklist', contextDisplayName: 'CM Daily Checklist', contextDescription: 'Action items from Centre Manager daily property checklists and walkthrough findings.', icon: 'fa-tasks', sortOrder: 3 },
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'ROMDailyChecklist', contextDisplayName: 'ROM Daily Checklist', contextDescription: 'Action items from Regional Operations Manager daily oversight checklists.', icon: 'fa-tasks', sortOrder: 4 },
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'AssetService', contextDisplayName: 'Asset Service', contextDescription: 'Action items generated when assets are due for scheduled servicing or require Repairs & Maintenance approval.', icon: 'fa-cogs', sortOrder: 5 },
    { moduleKey: 'Operations', moduleDisplayName: 'Operations', contextKey: 'AssetRnM', contextDisplayName: 'Asset Repairs & Maintenance', contextDescription: 'Action items for ad-hoc asset repairs and maintenance requiring Procurement approval.', icon: 'fa-wrench', sortOrder: 6 },

    // Procurement - vendor management and purchasing
    { moduleKey: 'Procurement', moduleDisplayName: 'Procurement', contextKey: 'VendorApplication', contextDisplayName: 'Vendor Application', contextDescription: 'Action items for processing new vendor applications and onboarding.', icon: 'fa-user-plus', sortOrder: 1 },
    { moduleKey: 'Procurement', moduleDisplayName: 'Procurement', contextKey: 'ProcurementPack', contextDisplayName: 'Procurement Pack', contextDescription: 'Action items for procurement package reviews, approvals, and processing.', icon: 'fa-shopping-cart', sortOrder: 2 },

    // Finance - payments and financial tracking
    { moduleKey: 'Finance', moduleDisplayName: 'Finance', contextKey: 'PaymentTrackers', contextDisplayName: 'Payment Trackers', contextDescription: 'Action items for tracking payments, invoices, and financial follow-ups.', icon: 'fa-money-bill-wave', sortOrder: 1 },

    // Leasing - tenant and lease management
    { moduleKey: 'Leasing', moduleDisplayName: 'Leasing', contextKey: 'LeaseRenewal', contextDisplayName: 'Lease Renewal', contextDescription: 'Action items for lease renewal processes, negotiations, and documentation.', icon: 'fa-file-signature', sortOrder: 1 },

    // IT - technical support and development
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'SupportTicket', contextDisplayName: 'Support Ticket', contextDescription: 'Action items for IT support requests and technical assistance.', icon: 'fa-headset', sortOrder: 1 },
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'ChangeRequest', contextDisplayName: 'Change Request', contextDescription: 'Action items for IT infrastructure or system change requests.', icon: 'fa-exchange-alt', sortOrder: 2 },
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'Onboarding', contextDisplayName: 'IT Onboarding', contextDescription: 'Action items for new employee IT setup, accounts, and equipment provisioning.', icon: 'fa-user-check', sortOrder: 3 },
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'Offboarding', contextDisplayName: 'IT Offboarding', contextDescription: 'Action items for departing employee IT access removal and equipment return.', icon: 'fa-user-times', sortOrder: 4 },
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'SoftwareDevRequest', contextDisplayName: 'Software Dev Request', contextDescription: 'Action items for software development requests, feature enhancements, and bug fixes.', icon: 'fa-code', sortOrder: 5 },
    { moduleKey: 'IT', moduleDisplayName: 'IT', contextKey: 'NetworkMonitoring', contextDisplayName: 'Network Monitoring', contextDescription: 'Action items generated by the Router Monitoring system when network alerts are triggered (availability, CPU, memory, disk thresholds).', icon: 'fa-network-wired', sortOrder: 6 },

    // Projects - project management
    { moduleKey: 'Projects', moduleDisplayName: 'Projects', contextKey: 'ProjectTask', contextDisplayName: 'Project Task', contextDescription: 'Main project tasks and deliverables.', icon: 'fa-project-diagram', sortOrder: 1 },
    { moduleKey: 'Projects', moduleDisplayName: 'Projects', contextKey: 'ProjectSubTask', contextDisplayName: 'Project SubTask', contextDescription: 'Sub-tasks and work items under a main project task.', icon: 'fa-stream', sortOrder: 2 },
];

/**
 * ModuleTemplateConfigPage Component
 * 
 * Provides a simplified interface for assigning templates to predefined module contexts.
 * Users cannot add or remove contexts - only assign templates to existing ones.
 */
const ModuleTemplateConfigPage = ({ onNavigateToTab }) => {
    const { apiFetch, user } = useAuth();
    
    // State for module assignments from database
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    
    // State for templates dropdown
    const [templates, setTemplates] = useState([]);

    // State for email templates dropdown
    const [emailTemplates, setEmailTemplates] = useState([]);

    // State for boards and departments dropdowns
    const [boards, setBoards] = useState([]);
    const [departments, setDepartments] = useState([]);

    // State for filtering
    const [selectedModule, setSelectedModule] = useState('all');

    // State for back-fill feature
    const [backfillTarget, setBackfillTarget] = useState(null);
    const [backfillPreview, setBackfillPreview] = useState(null);
    const [backfillLoading, setBackfillLoading] = useState(false);
    const [backfillExecuting, setBackfillExecuting] = useState(false);
    const [backfillOnlyEmpty, setBackfillOnlyEmpty] = useState(true);

    /**
     * Preview the impact of back-filling a context's defaults to existing items
     */
    const handleBackfillPreview = async (context) => {
        setBackfillTarget(context);
        setBackfillPreview(null);
        setBackfillLoading(true);
        setBackfillOnlyEmpty(true);
        try {
            const res = await apiFetch(`/api/ModuleTemplateAssignments/${context.id}/backfill-preview`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    assignmentId: context.id,
                    updateBoard: !!context.defaultBoardId,
                    updateDepartment: !!context.defaultDepartmentId,
                    onlyFillEmpty: true,
                    userId: user?.userID || user?.id || 0
                })
            });
            const data = await res.json();
            if (data.success) {
                setBackfillPreview(data.data);
            } else {
                toast.error(data.message || 'Failed to preview back-fill');
                setBackfillTarget(null);
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Failed to preview back-fill');
            setBackfillTarget(null);
        } finally {
            setBackfillLoading(false);
        }
    };

    /**
     * Execute the back-fill operation
     */
    const handleBackfillExecute = async () => {
        if (!backfillTarget) return;
        setBackfillExecuting(true);
        try {
            const res = await apiFetch(`/api/ModuleTemplateAssignments/${backfillTarget.id}/backfill`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    assignmentId: backfillTarget.id,
                    updateBoard: !!backfillTarget.defaultBoardId,
                    updateDepartment: !!backfillTarget.defaultDepartmentId,
                    onlyFillEmpty: backfillOnlyEmpty,
                    userId: user?.userID || user?.id || 0
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message || `Updated ${data.updatedCount} items`);
                setBackfillTarget(null);
                setBackfillPreview(null);
            } else {
                toast.error(data.message || 'Back-fill failed');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Back-fill failed');
        } finally {
            setBackfillExecuting(false);
        }
    };

    /**
     * Fetch module template assignments from API
     */
    const fetchModuleAssignments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch('/api/ModuleTemplateAssignments?includeInactive=false');
            const data = await response.json();
            
            if (data.success) {
                setModules(data.data);
            } else {
                toast.error('Failed to load module configurations');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching module assignments:', error);
            toast.error('Error loading module configurations');
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    /**
     * Fetch available templates for dropdown
     */
    const fetchTemplates = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Templates?includeInactive=false');
            const data = await response.json();

            if (data.success) {
                setTemplates(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching templates:', error);
        }
    }, [apiFetch]);

    /**
     * Fetch available email templates for dropdown
     */
    const fetchEmailTemplates = useCallback(async () => {
        try {
            const response = await apiFetch('/api/EmailTemplates');
            const data = await response.json();

            if (data.success) {
                setEmailTemplates(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching email templates:', error);
        }
    }, [apiFetch]);

    /**
     * Fetch boards for the default board dropdown
     */
    const fetchBoards = useCallback(async () => {
        try {
            const response = await apiFetch('/api/KanbanBoards');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setBoards(data.data.filter(b => b.isActive !== false));
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching boards:', error);
        }
    }, [apiFetch]);

    /**
     * Fetch departments for the default department dropdown
     */
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department?includeInactive=false');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setDepartments(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching departments:', error);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchModuleAssignments();
        fetchTemplates();
        fetchEmailTemplates();
        fetchBoards();
        fetchDepartments();
    }, [fetchModuleAssignments, fetchTemplates, fetchEmailTemplates, fetchBoards, fetchDepartments]);

    /**
     * Seed predefined contexts to the database if they don't exist.
     * This is called when the user clicks "Initialize Contexts" button.
     */
    const handleSeedContexts = async () => {
        setSeeding(true);
        try {
            const response = await apiFetch('/api/ModuleTemplateAssignments/seed', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contexts: PREDEFINED_CONTEXTS,
                    userId: user.userID
                })
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Initialized ${data.createdCount} new context(s)`);
                fetchModuleAssignments();
            } else {
                toast.error(data.message || 'Failed to initialize contexts');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error seeding contexts:', error);
            toast.error('Error initializing contexts');
        } finally {
            setSeeding(false);
        }
    };

    /**
     * Auto-save a single assignment change immediately
     * @param {number} contextId - The context assignment ID
     * @param {object} changes - The changes to save
     */
    const autoSaveAssignment = useCallback(async (contextId, changes) => {
        setSaving(true);
        try {
            const response = await apiFetch('/api/ModuleTemplateAssignments/bulk', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    assignments: [{ id: parseInt(contextId), ...changes }],
                    userId: user.userID
                })
            });

            // Handle non-JSON responses (e.g., HTML error pages) gracefully
            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                Sentry.captureException(parseError);
                toast.error(`Server returned ${response.status}: unable to parse response`);
                return;
            }

            if (!response.ok) {
                // ASP.NET Core validation errors have a 'title' and 'errors' shape
                if (data.title) {
                    const validationErrors = data.errors
                        ? Object.values(data.errors).flat().join('; ')
                        : data.title;
                    toast.error(validationErrors);
                } else {
                    toast.error(data.message || `Save failed (${response.status})`);
                }
                return;
            }

            if (data.success) {
                if (data.errors && data.errors.length > 0) {
                    data.errors.forEach(err => toast.error(err));
                } else {
                    toast.success('Assignment saved');
                }
                fetchModuleAssignments();
            } else {
                toast.error(data.message || 'Failed to save');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error(`Error saving assignment: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    }, [apiFetch, user, fetchModuleAssignments]);

    /**
     * Handle template selection change for a context
     * @param {number} contextId - The context assignment ID
     * @param {string} templateKey - The selected template key
     */
    const handleTemplateChange = (contextId, templateKey) => {
        autoSaveAssignment(contextId, { assignedTemplateKey: templateKey || '' });
    };

    /**
     * Handle email template selection change for a context
     * @param {number} contextId - The context assignment ID
     * @param {string} emailTemplateKey - The selected email template key
     */
    const handleEmailTemplateChange = (contextId, emailTemplateKey) => {
        autoSaveAssignment(contextId, { assignedEmailTemplateKey: emailTemplateKey || '' });
    };

    /**
     * Handle default board selection change for a context
     */
    const handleDefaultBoardChange = (contextId, boardId) => {
        autoSaveAssignment(contextId, { defaultBoardId: boardId ? parseInt(boardId) : 0 });
    };

    /**
     * Handle default department selection change for a context
     */
    const handleDefaultDepartmentChange = (contextId, departmentId) => {
        autoSaveAssignment(contextId, { defaultDepartmentId: departmentId ? parseInt(departmentId) : 0 });
    };

    /**
     * Get the current template value for a context
     * @param {object} context - The context object
     * @returns {string} The template key
     */
    const getContextTemplateValue = (context) => {
        return context.assignedTemplateKey || '';
    };

    /**
     * Get the current email template value for a context
     * @param {object} context - The context object
     * @returns {string} The email template key
     */
    const getContextEmailTemplateValue = (context) => {
        return context.assignedEmailTemplateKey || '';
    };

    /**
     * Get the current default board value for a context
     */
    const getContextDefaultBoardValue = (context) => {
        return context.defaultBoardId || '';
    };

    /**
     * Get the current default department value for a context
     */
    const getContextDefaultDepartmentValue = (context) => {
        return context.defaultDepartmentId || '';
    };

    /**
     * Get filtered modules based on selection
     */
    const getFilteredModules = () => {
        if (selectedModule === 'all') {
            return modules;
        }
        return modules.filter(m => m.moduleKey === selectedModule);
    };

    /**
     * Get unique module keys for the filter dropdown
     */
    const getUniqueModules = () => {
        return modules.map(m => ({
            key: m.moduleKey,
            displayName: m.moduleDisplayName
        }));
    };

    /**
     * Check if any predefined contexts are missing from the database.
     * modules is grouped: [{moduleKey, contexts: [{contextKey, ...}]}]
     * We must look inside each module group's contexts array.
     */
    const missingContextCount = !loading ? PREDEFINED_CONTEXTS.filter(pc =>
        !modules.some(m => m.moduleKey === pc.moduleKey &&
            m.contexts?.some(c => c.contextKey === pc.contextKey))
    ).length : 0;
    const needsSeeding = !loading && modules.length === 0;
    const hasMissingContexts = !loading && missingContextCount > 0;

    const filteredModules = getFilteredModules();

    return (
        <div className="module-template-config-page">


            {/* Page Header */}
            <div className="page-header">
                <div className="header-left">
                    <h1>
                        <FaPuzzlePiece /> Module Template Configuration
                    </h1>
                    <p className="page-description">
                        Assign templates to different module contexts. Each context represents a specific function where action items can be created.
                    </p>
                </div>
                <div className="header-actions">
                    {onNavigateToTab && (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => onNavigateToTab('templateManagement')}
                            title="Go to Item Templates to design the form fields for each action item type"
                        >
                            ← Back: Item Templates
                        </button>
                    )}
                    {onNavigateToTab && (
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => onNavigateToTab('boardCreationRules')}
                            title="Go to Board Creation Rules to control who sees each type in the Create Action Item selector"
                        >
                            Next: Board Creation Rules →
                        </button>
                    )}
                    {hasMissingContexts && (
                        <button 
                            className="btn btn-outline-secondary"
                            onClick={handleSeedContexts}
                            disabled={seeding}
                            title={`${missingContextCount} new context(s) available to add`}
                        >
                            {seeding ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Syncing...
                                </>
                            ) : (
                                <>
                                    <FaDatabase /> {modules.length === 0 ? 'Initialize Contexts' : `Sync Contexts (${missingContextCount} new)`}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Info Box */}
            <div className="info-box">
                <FaInfoCircle />
                <div>
                    <strong>Step 2 of 3: Module Assignments</strong>
                    <p>
                        Assign a template to each context so the system knows which form to use when an action item is created
                        (e.g. the <em>IT / Support Ticket</em> context should use your IT support request template).
                        Once templates are assigned here, go to <strong>Board Creation Rules</strong> to choose which of these
                        contexts appear in the &quot;Create Action Item&quot; type selector and who is allowed to use them.
                    </p>
                    <details className="info-details" style={{ marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem', color: '#2563eb' }}>What do the four columns mean?</summary>
                        <ul style={{ margin: '6px 0 0 16px', fontSize: '0.82rem', lineHeight: 1.6, color: '#374151' }}>
                            <li><strong>Action Item Template</strong> — the form fields shown when a user creates this type of action item (designed under <em>Item Templates</em>).</li>
                            <li><strong>Email Template</strong> — the email layout used when notifications are sent for this type (designed under <em>Email Templates</em>).</li>
                            <li><strong>Default Board</strong> — the Kanban board new items of this type are added to automatically.</li>
                            <li><strong>Default Department</strong> — the department automatically assigned to new items of this type.</li>
                        </ul>
                    </details>
                </div>
            </div>

            {/* Filters */}
            {modules.length > 0 && (
                <div className="filter-bar">
                    <div className="filter-group">
                        <label htmlFor="moduleFilter">Filter by Module:</label>
                        <select 
                            id="moduleFilter"
                            className="form-control"
                            value={selectedModule}
                            onChange={(e) => setSelectedModule(e.target.value)}
                        >
                            <option value="all">All Modules</option>
                            {getUniqueModules().map(m => (
                                <option key={m.key} value={m.key}>{m.displayName}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading module configurations...</p>
                </div>
            ) : needsSeeding ? (
                /* Empty State - Need to Seed */
                <div className="empty-state">
                    <FaDatabase className="fa-4x" />
                    <h2>No Contexts Found</h2>
                    <p>
                        The module contexts have not been initialized yet. 
                        Click the button below to create the predefined contexts.
                    </p>
                    <button 
                        className="btn btn-primary btn-lg"
                        onClick={handleSeedContexts}
                        disabled={seeding}
                    >
                        {seeding ? (
                            <>
                                <FaSpinner className="fa-spin" /> Initializing...
                            </>
                        ) : (
                            <>
                                <FaDatabase /> Initialize Contexts
                            </>
                        )}
                    </button>
                </div>
            ) : (
                /* Module Cards */
                <div className="modules-container">
                    {filteredModules.map(module => (
                        <div key={module.moduleKey} className="module-card">
                            <div className="module-header">
                                <h2>
                                    {resolveIcon(getModuleIcon(module.moduleKey))}
                                    {module.moduleDisplayName}
                                </h2>
                                <span className="context-count">
                                    {module.contexts.length} context{module.contexts.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            <div className="contexts-list">
                                {module.contexts.map(context => (
                                    <div 
                                        key={context.id} 
                                        className="context-row"
                                    >
                                        <div className="context-info">
                                            <div className="context-icon">
                                                {resolveIcon(context.icon || 'fa-cog')}
                                            </div>
                                            <div className="context-details">
                                                <strong>{context.contextDisplayName}</strong>
                                                <span className="context-description">
                                                    {context.contextDescription || 'No description available'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="context-templates">
                                            <div className="context-template">
                                                <label className="template-label">Action Item Template</label>
                                                <select
                                                    className="form-control template-select"
                                                    value={getContextTemplateValue(context)}
                                                    onChange={(e) => handleTemplateChange(context.id, e.target.value)}
                                                >
                                                    <option value="">-- No Template Assigned --</option>
                                                    {templates.map(t => (
                                                        <option key={t.templateKey} value={t.templateKey}>
                                                            {t.displayName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="context-template">
                                                <label className="template-label">Email Template</label>
                                                <select
                                                    className="form-control template-select"
                                                    value={getContextEmailTemplateValue(context)}
                                                    onChange={(e) => handleEmailTemplateChange(context.id, e.target.value)}
                                                >
                                                    <option value="">-- No Email Template --</option>
                                                    {emailTemplates.map(et => (
                                                        <option key={et.templateKey} value={et.templateKey}>
                                                            {et.displayName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="context-template">
                                                <label className="template-label">Default Board</label>
                                                <select
                                                    className="form-control template-select"
                                                    value={getContextDefaultBoardValue(context)}
                                                    onChange={(e) => handleDefaultBoardChange(context.id, e.target.value)}
                                                >
                                                    <option value="">-- No Default Board --</option>
                                                    {boards.map(b => (
                                                        <option key={b.boardId} value={b.boardId}>
                                                            {b.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="context-template">
                                                <label className="template-label">Default Department</label>
                                                <select
                                                    className="form-control template-select"
                                                    value={getContextDefaultDepartmentValue(context)}
                                                    onChange={(e) => handleDefaultDepartmentChange(context.id, e.target.value)}
                                                >
                                                    <option value="">-- No Default Department --</option>
                                                    {departments.map(d => (
                                                        <option key={d.departmentID} value={d.departmentID}>
                                                            {d.departmentName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Back-fill button: apply current defaults to existing action items */}
                                            {(context.defaultBoardId || context.defaultDepartmentId) && (
                                                <div className="context-template">
                                                    <label className="template-label">Apply to Existing</label>
                                                    <button
                                                        className="btn-backfill"
                                                        onClick={() => handleBackfillPreview(context)}
                                                        title="Apply current board/department settings to existing action items of this type"
                                                    >
                                                        <FaStream size={12} /> Back-fill Existing Items
                                                    </button>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Back-fill Confirmation Modal */}
            {backfillTarget && (
                <div className="backfill-overlay" onClick={() => { setBackfillTarget(null); setBackfillPreview(null); }}>
                    <div className="backfill-modal" onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 12px', fontSize: '1.1rem' }}>
                            Back-fill: {backfillTarget.contextDisplayName || backfillTarget.contextKey}
                        </h3>

                        {backfillLoading ? (
                            <p style={{ color: '#6b7280' }}><FaSpinner className="fa-spin" /> Analyzing existing action items...</p>
                        ) : backfillPreview ? (
                            <div>
                                <p style={{ marginBottom: '12px', color: '#374151' }}>
                                    Found <strong>{backfillPreview.totalMatching}</strong> existing action items of type
                                    &quot;{backfillPreview.contextDisplayName || backfillPreview.contextKey}&quot;.
                                </p>

                                {backfillPreview.newBoardName && (
                                    <p style={{ marginBottom: '8px' }}>
                                        📋 <strong>Board:</strong> Will assign to &quot;{backfillPreview.newBoardName}&quot;
                                        — <strong>{backfillPreview.wouldUpdateBoard}</strong> items would be updated
                                    </p>
                                )}

                                {backfillPreview.newDepartmentName && (
                                    <p style={{ marginBottom: '8px' }}>
                                        🏢 <strong>Department:</strong> Will assign to &quot;{backfillPreview.newDepartmentName}&quot;
                                        — <strong>{backfillPreview.wouldUpdateDepartment}</strong> items would be updated
                                    </p>
                                )}

                                <div style={{ margin: '16px 0', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={backfillOnlyEmpty}
                                            onChange={(e) => setBackfillOnlyEmpty(e.target.checked)}
                                        />
                                        <span>Only update items that don&apos;t already have a value assigned (recommended)</span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                                    <button
                                        className="btn-cancel"
                                        onClick={() => { setBackfillTarget(null); setBackfillPreview(null); }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-primary"
                                        onClick={handleBackfillExecute}
                                        disabled={backfillExecuting || (backfillPreview.wouldUpdateBoard === 0 && backfillPreview.wouldUpdateDepartment === 0)}
                                    >
                                        {backfillExecuting ? 'Applying...' : 'Apply Back-fill'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p style={{ color: '#dc2626' }}>Failed to load preview data.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Get icon class for a module based on its key
 * @param {string} moduleKey - The module key
 * @returns {string} Font Awesome icon class
 */
const getModuleIcon = (moduleKey) => {
    const icons = {
        'General': 'fa-plus-circle',
        'Operations': 'fa-hard-hat',
        'Procurement': 'fa-shopping-cart',
        'Finance': 'fa-chart-line',
        'Leasing': 'fa-file-signature',
        'IT': 'fa-laptop-code',
        'Projects': 'fa-project-diagram',
        'Legal': 'fa-gavel',
        'HR': 'fa-users'
    };
    return icons[moduleKey] || 'fa-puzzle-piece';
};

export default ModuleTemplateConfigPage;
